import { useEffect, useRef, useState } from 'react';
import feature2 from '~/landing_page/Assets/feature2.png';
import { DeckIcon } from '~/design-system/elements/DeckIcon/DeckIcon';
import { FeatureDemoScaleFit } from './FeatureDemoScaleFit';
import { usePrefersReducedMotion } from './usePrefersReducedMotion';
import { cn } from '~/lib/utils';
import styles from './FeatureContextDemo.module.css';

// Figma: "Context-new animation" (Landing-page-V2, section 98:4929).
// Window is 420x502; every color/spacing/radius below resolves to design tokens.
const DESIGN_WIDTH = 420;
const DESIGN_HEIGHT = 502;

const ASSETS = '/landing/features/context-demo';

type Phase =
  | 'idle'
  | 'sent'
  | 'thinking'
  | 'figma'
  | 'github'
  | 'linear'
  | 'notion'
  | 'summary';

const SOURCE_PHASES = ['figma', 'github', 'linear', 'notion'] as const;
type SourcePhase = (typeof SOURCE_PHASES)[number];

const isSourcePhase = (p: Phase): p is SourcePhase =>
  (SOURCE_PHASES as readonly string[]).includes(p);

// Timings: collapsed row holds, then auto-expands (Guri: "opens for maybe two
// seconds"), rows tick through reading→read, then the next source takes over.
const IDLE_MS = 1400;
const SENT_MS = 1000;
const THINKING_MS = 2200;
const COLLAPSED_MS = 1700;
const FIGMA_EXPANDED_MS = 2600;
const ROW_TICK_MS = 650;
const DONE_HOLD_MS = 700;
const SWAP_FADE_MS = 200;
const SUMMARY_HOLD_MS = 5200;
const RESTART_FADE_MS = 450;

interface ListRowData {
  id?: string;
  title: string;
  meta?: string;
}

interface SourceConfig {
  icon: string;
  iconExpanded?: string;
  collapsedLabel: string;
  expandedLabel: string;
  rows?: ListRowData[];
  /** Notion rows: single muted title column, no identifier. */
  mutedTitles?: boolean;
}

const SOURCES: Record<SourcePhase, SourceConfig> = {
  figma: {
    icon: `${ASSETS}/figma-logo.svg`,
    collapsedLabel: 'Seeing what changed since last sprint',
    expandedLabel: 'Figma file: Cart · Payment · Order confirmed',
  },
  github: {
    icon: `${ASSETS}/github-mark.svg`,
    collapsedLabel: 'Reading through the designs and the code',
    expandedLabel: 'Read 4 commits in deck-ui',
    rows: [
      { id: 'a1f3c92', title: 'Add one-page checkout', meta: 'guri' },
      { id: '7b2e104', title: 'Support Apple Pay and Google Pay', meta: 'beka' },
      { id: 'c93d4a1', title: 'Retry a declined payment automatically', meta: 'guri' },
      { id: '5d8b210', title: 'Send the order confirmation email', meta: 'beka' },
    ],
  },
  linear: {
    icon: `${ASSETS}/linear-logo.svg`,
    collapsedLabel: 'Pulling in what you closed out',
    expandedLabel: 'Reading Linear issues',
    rows: [
      { id: 'SHOP-212', title: 'Redesign the checkout flow' },
      { id: 'SHOP-218', title: 'Cut mobile cart abandonment' },
      { id: 'SHOP-224', title: 'Add express payment options' },
      { id: 'SHOP-231', title: 'Guest checkout without an account' },
    ],
  },
  notion: {
    icon: `${ASSETS}/notion-logo.svg`,
    iconExpanded: `${ASSETS}/notion-fill.svg`,
    collapsedLabel: 'Checking Project documents',
    expandedLabel: 'Reading 4 Notion pages',
    mutedTitles: true,
    rows: [
      { title: 'Checkout redesign PRD' },
      { title: 'Cart abandonment findings' },
      { title: 'Payment provider comparison' },
      { title: 'Launch readiness checklist' },
    ],
  },
};

const FIGMA_TILES = [
  { src: `${ASSETS}/artboard-cart.png`, alt: 'Cart artboard' },
  { src: `${ASSETS}/artboard-payment.png`, alt: 'Payment artboard' },
  { src: `${ASSETS}/artboard-order-confirmed.png`, alt: 'Order confirmed artboard' },
];

const USER_TEXT =
  'Put together a deck on the checkout redesign we just shipped, for the team review Thursday.';

const SUMMARY_INTRO = "Okay, I've got everything.";
const SUMMARY_PARAS = [
  'You shipped a full checkout redesign this sprint.',
  'The Figma frames walk from cart to confirmation, the commits show one-page checkout, Apple and Google Pay, and guest checkout going in, and the Linear issues you closed match every piece.',
  'Your PRD covers the why, and the metrics show cart abandonment dropping after launch.',
];

const CHIP_ICONS = [
  { src: `${ASSETS}/figma-logo.svg`, size: 10, alt: 'Figma' },
  { src: `${ASSETS}/linear-logo.svg`, size: 12, alt: 'Linear' },
  { src: `${ASSETS}/github-mark.svg`, size: 12, alt: 'GitHub' },
];

// Debug freeze: /route?ctxdemo=github-x pins a state for pixel comparison.
const readFrozenState = (): { phase: Phase; expanded: boolean } | null => {
  if (typeof window === 'undefined') return null;
  const raw = new URLSearchParams(window.location.search).get('ctxdemo');
  if (!raw) return null;
  const expanded = raw.endsWith('-x');
  const phase = (expanded ? raw.slice(0, -2) : raw) as Phase;
  const valid: Phase[] = ['idle', 'sent', 'thinking', 'figma', 'github', 'linear', 'notion', 'summary'];
  return valid.includes(phase) ? { phase, expanded } : null;
};

const RowStatusIcon = ({ state }: { state: 'done' | 'reading' | 'pending' }) => {
  if (state === 'done') return <img src={`${ASSETS}/commit-check.svg`} alt="" />;
  if (state === 'reading') return <span className={styles.spinner} />;
  return <img src={`${ASSETS}/commit-pending.svg`} alt="" />;
};

export interface FeatureContextDemoProps {
  /**
   * Window size in design px. The landing uses the Figma 420x502; the
   * /internal/ig-record page passes a wider and taller window so every expanded
   * source fits inside the thread and the window fills the recording slot.
   */
  designWidth?: number;
  designHeight?: number;
  /**
   * Pin the thread to the top: no follow-scroll, so the user message and the
   * activity headline never move and expanding content only grows downward.
   * Only makes sense with a designHeight tall enough to fit the largest state.
   */
  pinTop?: boolean;
}

export const FeatureContextDemo = ({
  designWidth = DESIGN_WIDTH,
  designHeight = DESIGN_HEIGHT,
  pinTop = false,
}: FeatureContextDemoProps = {}) => {
  const reducedMotion = usePrefersReducedMotion();
  const wrapRef = useRef<HTMLDivElement>(null);
  const threadRef = useRef<HTMLDivElement>(null);
  const [started, setStarted] = useState(false);

  // Frozen state is applied AFTER mount so SSR and first client render match.
  const [frozen, setFrozen] = useState<{ phase: Phase; expanded: boolean } | null>(null);

  const [phase, setPhase] = useState<Phase>('idle');
  const [expanded, setExpanded] = useState(false);
  // Rows completed in the active list; index === rowsDone is the "reading" row.
  const [rowsDone, setRowsDone] = useState(0);
  const [visible, setVisible] = useState(true);
  // Dissolves the whole activity row (icon included) between sources.
  const [rowSwapping, setRowSwapping] = useState(false);

  useEffect(() => {
    const f = readFrozenState();
    if (!f) return;
    setFrozen(f);
    setPhase(f.phase);
    setExpanded(f.expanded);
    setRowsDone(2);
  }, []);

  useEffect(() => {
    if (reducedMotion) return;
    const el = wrapRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setStarted(true);
          observer.unobserve(el);
        }
      },
      { root: null, rootMargin: '0px 0px -10% 0px', threshold: 0.25 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [reducedMotion]);

  // Scripted timeline.
  useEffect(() => {
    if (!started || reducedMotion || frozen) return;
    const timers: number[] = [];
    const later = (fn: () => void, ms: number) => timers.push(window.setTimeout(fn, ms));

    // Fade the whole row out, advance, fade the next source in.
    const swapTo = (target: Phase, delay: number) => {
      later(() => setRowSwapping(true), delay);
      later(() => {
        setExpanded(false);
        setRowsDone(0);
        setRowSwapping(false);
        setPhase(target);
      }, delay + SWAP_FADE_MS);
    };

    if (phase === 'idle') {
      later(() => setPhase('sent'), IDLE_MS);
    } else if (phase === 'sent') {
      later(() => setPhase('thinking'), SENT_MS);
    } else if (phase === 'thinking') {
      swapTo('figma', THINKING_MS);
    } else if (isSourcePhase(phase)) {
      const i = SOURCE_PHASES.indexOf(phase);
      const nextPhase: Phase = i === SOURCE_PHASES.length - 1 ? 'summary' : SOURCE_PHASES[i + 1];
      if (!expanded) {
        later(() => setExpanded(true), COLLAPSED_MS);
      } else if (phase === 'figma') {
        swapTo(nextPhase, FIGMA_EXPANDED_MS);
      } else {
        const total = SOURCES[phase].rows?.length ?? 0;
        if (rowsDone < total) {
          later(() => setRowsDone((n) => n + 1), ROW_TICK_MS);
        } else {
          swapTo(nextPhase, DONE_HOLD_MS);
        }
      }
    } else if (phase === 'summary') {
      later(() => setVisible(false), SUMMARY_HOLD_MS);
      later(() => {
        setPhase('idle');
        setExpanded(false);
        setRowsDone(0);
        setVisible(true);
      }, SUMMARY_HOLD_MS + RESTART_FADE_MS);
    }

    return () => {
      for (const t of timers) window.clearTimeout(t);
    };
  }, [started, reducedMotion, frozen, phase, expanded, rowsDone]);

  // Keep the newest content in view, like a real chat. Re-run after the reveal
  // transition (280ms) so expanding content is not left clipped behind the bar.
  // The pinned (record) variant skips this entirely: its window fits every
  // state, so the thread never scrolls and the top part never bumps.
  useEffect(() => {
    if (pinTop) return;
    const el = threadRef.current;
    if (!el) return;
    if (phase === 'summary') {
      el.scrollTo({ top: 0 });
      return;
    }
    const toBottom = () => el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    toBottom();
    const t1 = window.setTimeout(toBottom, 200);
    const t2 = window.setTimeout(toBottom, 420);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [pinTop, phase, expanded, rowsDone]);

  if (reducedMotion) {
    return (
      <img
        className={styles.fallback}
        src={feature2}
        alt="Deck pulling context from Figma, GitHub, Linear, Notion, and your uploads"
      />
    );
  }

  // Design's final frame shows the summary alone, centered — the message scrolls away.
  const showMessage = phase !== 'idle' && phase !== 'summary';
  const showThinking = phase === 'thinking';
  const activeSource = isSourcePhase(phase) ? SOURCES[phase] : null;
  const listTotal = activeSource?.rows?.length ?? 0;
  const listComplete = rowsDone >= listTotal;
  const placeholder = phase === 'idle' ? 'What did you accomplish today?' : 'Ask, reply anything';

  return (
    <div ref={wrapRef} className={styles.wrap}>
      <FeatureDemoScaleFit
        designWidth={designWidth}
        designHeight={designHeight}
        scaleMode="transform"
      >
        <div className={cn(styles.window, !visible && styles.windowHidden)}>
          {/* Header */}
          <div className={styles.header}>
            <div className={styles.headerLeft}>
              <DeckIcon name="chat-1" size={16} />
              <span className={styles.headerTitle}>Chat agent</span>
            </div>
            <div className={styles.headerRight}>
              <span className={styles.headerBtn}>
                <DeckIcon name="chat-side" size={16} />
              </span>
              <span className={styles.headerBtn}>
                <DeckIcon name="close" size={16} />
              </span>
            </div>
          </div>

          {/* Thread */}
          <div ref={threadRef} className={styles.thread}>
            <div className={cn(styles.threadInner, phase === 'summary' && styles.threadInnerCenter)}>
              {showMessage && (
                <div className={styles.userGroup}>
                  <div className={styles.attachRow}>
                    <div className={cn(styles.attachImage, styles.fadeIn)}>
                      <img src={`${ASSETS}/attachment-image.png`} alt="Checkout metrics" />
                    </div>
                    <div className={cn(styles.attachFile, styles.fadeIn)} style={{ animationDelay: '60ms' }}>
                      <span className={styles.attachType}>PDF</span>
                      <span className={styles.attachName}>research-summary</span>
                    </div>
                    <div className={cn(styles.attachFile, styles.fadeIn)} style={{ animationDelay: '120ms' }}>
                      <span className={styles.attachType}>MD</span>
                      <span className={styles.attachName}>rollout-plan</span>
                    </div>
                  </div>
                  <div className={cn(styles.bubble, styles.fadeIn)} style={{ animationDelay: '180ms' }}>
                    {USER_TEXT}
                  </div>
                  <div className={styles.sentSpacer} />
                </div>
              )}

              {/* Activity row: thinking indicator that becomes each source */}
              {(showThinking || activeSource) && (
                <div className={cn(styles.activity, rowSwapping && styles.activitySwapOut)}>
                  <div key={phase} className={styles.activityRow}>
                    <span className={styles.activityIcon}>
                      {showThinking ? (
                        <span className={styles.typingDots}>
                          <span className={styles.typingDot} />
                          <span className={styles.typingDot} />
                          <span className={styles.typingDot} />
                        </span>
                      ) : phase === 'figma' ? (
                        <DeckIcon name="figma" size={20} preserveColors />
                      ) : (
                        <img
                          src={
                            expanded && activeSource?.iconExpanded
                              ? activeSource.iconExpanded
                              : activeSource?.icon
                          }
                          alt=""
                        />
                      )}
                    </span>

                    <div className={styles.activityBody}>
                      <div className={styles.activityHeadline}>
                        <span
                          className={cn(
                            styles.activityLabel,
                            (showThinking || !expanded || !listComplete || phase === 'figma') &&
                              styles.labelShimmer,
                          )}
                        >
                          {showThinking
                            ? 'Reading your notes and metrics'
                            : expanded
                              ? activeSource?.expandedLabel
                              : activeSource?.collapsedLabel}
                        </span>
                        <span className={styles.activityChevron}>
                          <DeckIcon name={expanded ? 'arrow-up-s' : 'arrow-down-s'} size={20} />
                        </span>
                      </div>

                      {/* Expanded content */}
                      {activeSource && (
                        <div className={cn(styles.reveal, expanded && styles.revealOpen)}>
                          <div className={styles.revealInner}>
                            {phase === 'figma' ? (
                              <div className={styles.tilesRow}>
                                {FIGMA_TILES.map((tile, i) => (
                                  <div
                                    key={tile.src}
                                    className={cn(styles.tile, expanded && styles.fadeIn)}
                                    style={{ animationDelay: `${i * 70}ms` }}
                                  >
                                    <img src={tile.src} alt={tile.alt} />
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className={styles.listBox}>
                                {activeSource.rows?.map((row, i) => {
                                  const state =
                                    i < rowsDone ? 'done' : i === rowsDone ? 'reading' : 'pending';
                                  return (
                                    <div key={row.title} className={styles.listRow}>
                                      <span className={styles.rowStatus}>
                                        <RowStatusIcon state={state} />
                                      </span>
                                      {row.id && (
                                        <span
                                          className={cn(
                                            styles.rowId,
                                            state === 'reading' && styles.rowShimmer,
                                          )}
                                        >
                                          {row.id}
                                        </span>
                                      )}
                                      <span
                                        className={cn(
                                          styles.rowTitle,
                                          activeSource.mutedTitles && styles.rowTitleMuted,
                                          state === 'reading' && styles.rowShimmer,
                                        )}
                                      >
                                        {row.title}
                                      </span>
                                      {row.meta && <span className={styles.rowMeta}>{row.meta}</span>}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Agent summary */}
              {phase === 'summary' && (
                <div className={styles.summary}>
                  <div className={styles.fadeIn}>{SUMMARY_INTRO}</div>
                  <div className={cn(styles.fadeIn)} style={{ animationDelay: '150ms' }}>
                    {SUMMARY_PARAS.map((p) => (
                      <div key={p} className={styles.summaryPara}>
                        {p}
                      </div>
                    ))}
                  </div>
                  <div className={cn(styles.sourcesChip, styles.fadeIn)} style={{ animationDelay: '300ms' }}>
                    <span className={styles.chipStack}>
                      {CHIP_ICONS.map((icon) => (
                        <span key={icon.alt} className={styles.chipRing}>
                          <img src={icon.src} alt={icon.alt} width={icon.size} height={icon.size} />
                        </span>
                      ))}
                    </span>
                    <span className={styles.chipLabel}>24 Sources</span>
                  </div>
                  <div className={cn(styles.summaryActions, styles.fadeIn)} style={{ animationDelay: '420ms' }}>
                    <span className={styles.actionBtn}>
                      <DeckIcon name="Copy" size={20} />
                    </span>
                    <span className={styles.actionBtn}>
                      <DeckIcon name="like" size={20} />
                    </span>
                    <span className={styles.actionBtn}>
                      <DeckIcon name="dislike" size={20} />
                    </span>
                    <span className={styles.actionBtn}>
                      <DeckIcon name="redo" size={20} />
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Chat bar */}
          <div className={styles.chatbarContainer}>
            <div className={cn(styles.dotsHalo, phase !== 'idle' && styles.dotsHaloHidden)} />
            <div className={styles.chatbar}>
              <div className={styles.chatbarMiddle}>
                <span className={styles.placeholder}>{placeholder}</span>
              </div>
              <div className={styles.chatbarBottom}>
                <div className={styles.modelBtn}>
                  <DeckIcon name="brand-google" size={16} preserveColors />
                  <span className={styles.modelLabel}>Gemini 3.7 Flash</span>
                  <span className={styles.modelChevron}>
                    <DeckIcon name="arrow-down-s" size={16} />
                  </span>
                </div>
                <div className={styles.barRight}>
                  <span className={styles.iconBtn28}>
                    <DeckIcon name="add" size={20} />
                  </span>
                  <span className={styles.iconBtn28}>
                    <DeckIcon name="global" size={20} />
                  </span>
                  <span className={styles.sendBtn}>
                    <DeckIcon name="arrow-up" size={20} />
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </FeatureDemoScaleFit>
    </div>
  );
};

FeatureContextDemo.displayName = 'FeatureContextDemo';
